import { useEffect, useId, useRef, useState } from 'react';
import type { DragEvent, KeyboardEvent } from 'react';
import { ChartNoAxesColumn, ChevronDown, Plus, SquareFunction } from 'lucide-react';

export type AddInputKind = 'distribution' | 'formula';

interface AddInputMenuProps {
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  onAddDistribution: () => void;
  onAddFormula: () => void;
  onOptionDragStart?: (event: DragEvent<HTMLButtonElement>, inputKind: AddInputKind) => void;
}

const MENU_OPTIONS: Array<{
  kind: AddInputKind;
  label: string;
  description: string;
  icon: typeof ChartNoAxesColumn;
}> = [
  {
    kind: 'distribution',
    label: 'Distribution',
    description: 'Sample from fixed, normal, triangular, or uniform distributions.',
    icon: ChartNoAxesColumn
  },
  {
    kind: 'formula',
    label: 'Formula',
    description: 'Combine upstream variables using custom expressions.',
    icon: SquareFunction
  }
];

export const AddInputMenu = ({
  disabled = false,
  className = '',
  buttonClassName = '',
  onAddDistribution,
  onAddFormula,
  onOptionDragStart
}: AddInputMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const menuId = useId();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  const addInput = (kind: AddInputKind) => {
    if (disabled) {
      return;
    }

    if (kind === 'distribution') {
      onAddDistribution();
    } else {
      onAddFormula();
    }
    setIsOpen(false);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`add-input-menu nodrag nowheel ${isOpen ? 'is-open' : ''} ${className}`.trim()}
      onKeyDown={handleKeyDown}
    >
      <button
        type="button"
        className={`add-input-trigger ${buttonClassName}`.trim()}
        onClick={() => setIsOpen((current) => !current)}
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
      >
        <Plus size={15} strokeWidth={2.25} aria-hidden="true" />
        <span>Add input</span>
        <ChevronDown size={14} strokeWidth={2.25} aria-hidden="true" />
      </button>

      {isOpen && (
        <div id={menuId} className="add-input-options" role="menu">
          {MENU_OPTIONS.map((option) => {
            const Icon = option.icon;

            return (
              <button
                key={option.kind}
                type="button"
                className={`add-input-option add-input-option-${option.kind}`}
                onClick={() => addInput(option.kind)}
                onDragStart={(event) => onOptionDragStart?.(event, option.kind)}
                draggable={Boolean(onOptionDragStart)}
                role="menuitem"
              >
                <span className={`add-input-option-icon palette-icon-${option.kind}`}>
                  <Icon size={16} strokeWidth={2.25} aria-hidden="true" />
                </span>
                <span className="add-input-option-copy">
                  <span>{option.label}</span>
                  <span>{option.description}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
